import React, { useRef, useMemo, useState, useEffect } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { BackLink } from 'ui/BackLink';
import { useSelector } from 'react-redux';
import { ErrorBar, LoadingBar } from 'ui/NetworkStatusBar';
import { getBootstrapCountriesSelector } from 'store/modules/bootstrap/selectors';
import { useExploreHotelDetail, useExploreHotelPdfExport } from './hooks/useHotelCollectionQueries';
import FluidButton from 'ui/FluidButton';
import { MediaGalleryGrid } from 'ui/MediaGalleryGrid';
import { transformGalleryItems } from './utils/dataTransformers';
import { DesktopNavMenu, INavMenuItem } from './components/DesktopNavMenu';
import { RoomsAccordions } from './components/RoomsAccordions';
import { HotelDocuments } from './components/HotelDocuments';
import { HotelDetailsSection } from './components/HotelDetailsSection';
import { BottomTabs } from './components/BottomTabs';
import { ColumnBulletPoints } from 'ui/ColumnBulletPoints/ColumnBulletPoints';
import { useCurrentWidth } from 'effects';
import { ReviewSummaryBox } from 'containers/ReviewPage/ReviewSummaryBox';
import { useReviewPageQueries } from 'containers/ReviewPage/hooks/useReviewPageQueries';
import { AnnouncementsDisplayRender } from 'ui/AnnouncementsDisplay';
import { makeBookingManagerApi } from 'services/BookingManagerApi';
import { ParameterService } from 'services/ParametersProviderApi/ParametersService';
import { EUploadTag } from 'services/BackendApi';

interface IDetailParams {
  hotelUuid: string;
}

interface ILocationState {
  fromList?: string;
}

export const HotelCollectionDetail: React.FC = () => {
  const { hotelUuid } = useParams<IDetailParams>();
  const location = useLocation<ILocationState>();
  const isProgrammaticScroll = useRef<boolean>(false);

  // Get back link URL - use the referrer from list page if available, otherwise default to /hotels-collection
  const backLinkUrl = location.state?.fromList || '/hotels-collection';

  // Fetch hotel detail data
  const { data, isLoading, isError } = useExploreHotelDetail(hotelUuid);

  // PDF export mutation
  const exportPdf = useExploreHotelPdfExport(hotelUuid);

  // Fetch review data
  const dynamicParameters = ParameterService.getParameters();
  const { getHotelReviews } = useReviewPageQueries(makeBookingManagerApi(), hotelUuid);
  const reviewData = getHotelReviews.data?.data;

  // Fetch countries from bootstrap for country name display
  const bootstrapCountries = useSelector(getBootstrapCountriesSelector);

  const { isTabletOrMobile } = useCurrentWidth();

  // State for tracking active section in viewport
  const [activeSection, setActiveSection] = useState<string>('details');

  // Refs for scroll-to-section behavior
  const detailsRef = useRef<HTMLDivElement>(null);
  const announcementsRef = useRef<HTMLDivElement>(null);
  const roomsRef = useRef<HTMLDivElement>(null);
  const galleryRef = useRef<HTMLDivElement>(null);
  const reviewsRef = useRef<HTMLDivElement>(null);
  const documentsRef = useRef<HTMLDivElement>(null);
  const policiesRef = useRef<HTMLDivElement>(null);
  const locationRef = useRef<HTMLDivElement>(null);
  const contactsRef = useRef<HTMLDivElement>(null);

  // Compute derived values (must be before early returns to satisfy hooks rules)
  // Get hero image - prefer featured photo, fallback to first gallery item
  const heroImage = useMemo(() => {
    if (!data?.data.gallery) return null;
    const featuredPhoto = data.data.gallery.find(item => item.tag === EUploadTag.FEATURED_PHOTO);
    return featuredPhoto?.url || data.data.gallery[0]?.url || null;
  }, [data]);

  // Format location - show region, or country name as fallback
  const locationText = useMemo(() => {
    if (!data?.data) return '';

    if (data.data.region) {
      return data.data.region;
    }

    // Fallback to country name only when region is null
    const country = bootstrapCountries.find(c => c.code === data.data.countryCode);
    return country?.name || '';
  }, [data, bootstrapCountries]);

  // Determine which sections have content (must be before hooks that use these values)
  const hasAnnouncements = (data?.data?.announcements?.length ?? 0) > 0;
  const hasAccommodations = (data?.data?.accommodations?.length ?? 0) > 0;
  const hasGallery = (data?.data?.gallery?.length ?? 0) > 0;
  const hasDocuments = (data?.data?.documents?.length ?? 0) > 0;
  const hasPolicies = (data?.data?.policiesAndRestrictions?.length ?? 0) > 0;
  const hasContacts = !!(data?.data?.address || data?.data?.email || data?.data?.phoneNumber);
  const hasReviews = dynamicParameters.ENABLE_HOTEL_REVIEWS_TEMP && !!reviewData?.generalScore;

  // Combine summaries text for ReviewSummaryBox
  const reviewSummaryText = useMemo(() => {
    if (!reviewData?.summaries) return '';
    return [
      reviewData.summaries.guests?.replace(/\n/g, ''),
      reviewData.summaries.travelPartners?.replace(/\n/g, ''),
      reviewData.summaries.pureEscapes?.replace(/\n/g, ''),
    ]
      .filter(Boolean)
      .join('\n');
  }, [reviewData]);

  // Navigation menu items configuration
  const navMenuItems: INavMenuItem[] = useMemo(
    () => [
      { id: 'details', label: 'Hotel Details', isVisible: true },
      { id: 'announcements', label: 'Announcements', isVisible: hasAnnouncements },
      { id: 'rooms', label: 'Rooms', isVisible: hasAccommodations },
      { id: 'gallery', label: 'Hotel Gallery', isVisible: hasGallery },
      { id: 'reviews', label: 'Reviews', isVisible: hasReviews },
      { id: 'documents', label: 'Hotel Documents', isVisible: hasDocuments },
      { id: 'policies', label: 'Policies', isVisible: hasPolicies },
      { id: 'location', label: 'Location', isVisible: true },
      { id: 'contacts', label: 'Contacts', isVisible: hasContacts },
    ],
    [hasAnnouncements, hasAccommodations, hasGallery, hasReviews, hasDocuments, hasPolicies, hasContacts]
  );

  // Intersection Observer to track active section
  useEffect(() => {
    if (!data) return; // Only when data is available

    const observerOptions = {
      root: null,
      // Section is "active" when its top crosses the viewport top offset
      // Desktop uses a larger offset due to sticky header + left nav.
      rootMargin: isTabletOrMobile ? '-100px 0px -80% 0px' : '-160px 0px -80% 0px',
      threshold: 0,
    };

    const observerCallback = (entries: IntersectionObserverEntry[]) => {
      entries.forEach(entry => {
        if (entry.isIntersecting && !isProgrammaticScroll.current) {
          const sectionId = entry.target.getAttribute('data-section-id');
          if (sectionId) {
            setActiveSection(sectionId);
          }
        }
      });
    };

    const observer = new IntersectionObserver(observerCallback, observerOptions);

    // Observe all section refs
    const sections = [
      { ref: detailsRef, id: 'details' },
      { ref: announcementsRef, id: 'announcements' },
      { ref: roomsRef, id: 'rooms' },
      { ref: galleryRef, id: 'gallery' },
      { ref: reviewsRef, id: 'reviews' },
      { ref: documentsRef, id: 'documents' },
      { ref: policiesRef, id: 'policies' },
      { ref: locationRef, id: 'location' },
      { ref: contactsRef, id: 'contacts' },
    ];

    sections.forEach(({ ref }) => {
      if (ref.current) {
        observer.observe(ref.current);
      }
    });

    return () => {
      sections.forEach(({ ref }) => {
        if (ref.current) {
          observer.unobserve(ref.current);
        }
      });
    };
  }, [
    data,
    isTabletOrMobile,
    hasAnnouncements,
    hasAccommodations,
    hasGallery,
    hasReviews,
    hasDocuments,
    hasPolicies,
    hasContacts,
  ]);

  // Scroll to section handler
  const scrollToSection = (sectionId: string) => {
    isProgrammaticScroll.current = true;
    const refMap: Record<string, React.RefObject<HTMLDivElement | null>> = {
      details: detailsRef,
      announcements: announcementsRef,
      rooms: roomsRef,
      gallery: galleryRef,
      reviews: reviewsRef,
      documents: documentsRef,
      policies: policiesRef,
      location: locationRef,
      contacts: contactsRef,
    };

    const ref = refMap[sectionId];
    if (ref?.current) {
      const scrollOffset = isTabletOrMobile ? 140 : 160;
      // Calculate the target scroll position
      // Position element below the sticky header / top UI
      const elementTop = ref.current.getBoundingClientRect().top;
      const offsetPosition = elementTop + window.pageYOffset - scrollOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth',
      });
    }
    setTimeout(() => {
      isProgrammaticScroll.current = false;
    }, 600);
  };

  // Loading state
  if (isLoading) {
    return <LoadingBar />;
  }

  // Error state
  if (isError) {
    return (
      <div className="mx-[20px] crm-desktop:mx-[80px] mt-8">
        <ErrorBar message="Failed to load hotel details. Please try again." />
      </div>
    );
  }

  // 404 state
  if (!data) {
    return (
      <div className="mx-[20px] crm-desktop:mx-[80px] mt-8">
        <ErrorBar message="Hotel not found." />
      </div>
    );
  }

  const hotel = data.data;

  return (
    <div className="mx-[20px] crm-desktop:mx-[80px]">
      {/* Header row with Back link and Export PDF button */}
      <div className="flex justify-between items-center mt-[20px]">
        <BackLink type="internal" href={backLinkUrl}>
          Back to hotels collection
        </BackLink>

        <FluidButton
          type="secondary"
          onClick={() => exportPdf.mutate()}
          isLoading={exportPdf.isPending}
        >
          Export PDF
        </FluidButton>
      </div>

      {/* Hero Image Section */}
      <div className="relative w-full h-[250px] mt-[20px]">
        {/* Hero Image */}
        {heroImage ? (
          <img src={heroImage} alt={hotel.name} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full bg-gray-20" />
        )}

        {/* Dark overlay for text readability */}
        <div className="absolute inset-0 bg-black/30" />

        {/* PREFERRED Badge - Top Left */}
        {hotel.isPreferred && (
          <div className="absolute top-[15px] left-[15px] bg-brown-prime px-[10px] py-[5px]">
            <span className="font-hurmegeometric-sans font-semibold text-[14px] text-white uppercase tracking-wider">
              Preferred
            </span>
          </div>
        )}

        {/* Hotel Name & Location Overlay - Centered Floating Text */}
        <div className="absolute inset-0 flex flex-col items-center justify-center px-[20px] py-[20px]">
          <span className="block font-noe-display font-normal text-[36px] leading-[46px] lg:text-[72px] lg:leading-[92px] text-white text-center capitalize">
            {hotel.name}
          </span>
          <span className="block font-noe-display font-normal text-[26px] leading-[33px] lg:text-[36px] lg:leading-[46px] text-white text-center">
            {locationText}
          </span>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="mt-[40px]">
        {/* Responsive Grid: Single column on mobile/tablet, two-column on desktop */}
        <div className="grid grid-cols-1 lg:grid-cols-[180px_1fr] gap-[40px] lg:bg-[linear-gradient(to_right,#F4F4F3_0,#F4F4F3_180px,transparent_180px)]">
          {/* Left Navigation Panel - Desktop only */}
          <DesktopNavMenu items={navMenuItems} activeSection={activeSection} onScrollToSection={scrollToSection} />

          {/* Right Content Column */}
          <div className="flex flex-col gap-[60px]">
            {/* Hotel Details Section */}
            <div ref={detailsRef} data-section-id="details">
              <HotelDetailsSection
                name={hotel.name}
                description={hotel.description}
                mealPlanCategories={hotel.mealPlanCategories}
                amenities={hotel.amenities}
                highlights={hotel.highlights}
                inLoveWith={hotel.inLoveWith}
                additionalInfo={hotel.additionalInfo}
              />
            </div>

            {/* Announcements Section */}
            {hasAnnouncements && (
              <div ref={announcementsRef} data-section-id="announcements" className="flex flex-col gap-[10px]">
                <span className="block font-noe-display font-normal text-[26px] leading-[33px] text-black">
                  Announcements
                </span>
                <AnnouncementsDisplayRender
                  announcements={hotel.announcements}
                  className="!mt-0 !font-pt-sans rounded-[4px]"
                  titleClassName="uppercase"
                />
              </div>
            )}

            {/* Rooms Section */}
            {hasAccommodations && (
              <div ref={roomsRef} data-section-id="rooms" className="flex flex-col gap-[10px]">
                <span className="block font-noe-display font-normal text-[26px] leading-[33px] text-black">Rooms</span>
                <RoomsAccordions accommodations={hotel.accommodations} />
              </div>
            )}

            {/* Hotel Gallery Section */}
            {hasGallery && (
              <div ref={galleryRef} data-section-id="gallery" className="flex flex-col gap-[10px]">
                <span className="block font-noe-display font-normal text-[26px] leading-[33px] text-black">
                  Hotel Gallery
                </span>
                <MediaGalleryGrid
                  uploads={transformGalleryItems(
                    // Sort gallery items: featured photos first, then regular photos
                    // Backend already provides items in newest-first order
                    [...hotel.gallery].sort((a, b) => {
                      // Featured photos come first
                      if (a.tag === EUploadTag.FEATURED_PHOTO && b.tag !== EUploadTag.FEATURED_PHOTO) return -1;
                      if (a.tag !== EUploadTag.FEATURED_PHOTO && b.tag === EUploadTag.FEATURED_PHOTO) return 1;
                      // Otherwise maintain original order (already newest-first from backend)
                      return 0;
                    })
                  )}
                  baseUrlPath=""
                  titleForModal={`${hotel.name} - Hotel Gallery`}
                  enableBulkDownload
                />
              </div>
            )}

            {/* Reviews Section */}
            {hasReviews && reviewData && (
              <div ref={reviewsRef} data-section-id="reviews" className="flex flex-col gap-[10px]">
                <span className="block font-noe-display font-normal text-[26px] leading-[33px] text-black">
                  Reviews
                </span>
                <div className="border border-solid border-gray-20 p-5">
                  <ReviewSummaryBox
                    generalScore={reviewData.generalScore}
                    summaryText={reviewSummaryText}
                    detailedScores={reviewData.detailedScores}
                    hideDescription
                    summaryMaxLines={7}
                  />
                </div>
              </div>
            )}

            {/* Hotel Documents Section */}
            {hasDocuments && (
              <div ref={documentsRef} data-section-id="documents" className="flex flex-col gap-[10px]">
                <span className="block font-noe-display font-normal text-[26px] leading-[33px] text-black">
                  Hotel Documents
                </span>
                <HotelDocuments documents={hotel.documents} />
              </div>
            )}

            {/* Policies Section */}
            {hasPolicies && (
              <div ref={policiesRef} data-section-id="policies" className="flex flex-col gap-[10px]">
                <span className="block font-noe-display font-normal text-[26px] leading-[33px] text-black">
                  Policies
                </span>
                <ColumnBulletPoints
                  items={hotel.policiesAndRestrictions || []}
                  cols={1}
                  wrapperClassName=""
                  ulClassName="m-0"
                  liClassName="font-pt-sans text-[15px] leading-[22px] text-black"
                />
              </div>
            )}

            {/* Location Section - Placeholder until map integration */}
            <div ref={locationRef} data-section-id="location" className="flex flex-col gap-[10px]">
              <span className="block font-noe-display font-normal text-[26px] leading-[33px] text-black">Location</span>
              <span className="block font-pt-sans text-[15px] leading-[22px] text-gray-100 italic">
                Map integration coming soon
              </span>
            </div>

            {/* Contacts Section */}
            {hasContacts && (
              <div ref={contactsRef} data-section-id="contacts" className="flex flex-col gap-[10px]">
                <span className="block font-noe-display font-normal text-[26px] leading-[1.276em] text-black">
                  Contacts
                </span>
                <div className="p-[20px] border border-gray-20 bg-white">
                  <div className="flex flex-col font-pt-sans text-[15px] text-black">
                    <span className="font-bold text-[17px]">Hotel Contact</span>
                    {data.data.address && <span>{data.data.address}</span>}
                    {data.data.email && <span className="underline">{data.data.email}</span>}
                    {data.data.phoneNumber && <span>{data.data.phoneNumber}</span>}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Sticky bottom navigation (mobile/tablet only) */}
          <BottomTabs items={navMenuItems} activeSection={activeSection} onScrollToSection={scrollToSection} setActiveSection={setActiveSection} />
        </div>
      </div>
    </div>
  );
};
